import React from 'react';
import { Link } from 'react-router-dom';
import { PageShell } from '../design-system';
import { trustPageContent } from '../site/trustPageContent';
import PrerenderReady from '../components/PrerenderReady';
import './TrustPage.css';

type TrustPageKind = 'about' | 'contact';

type TrustPageProps = {
  page: TrustPageKind;
};

type ResourceLink = {
  label: string;
  url: string;
};

type TrustPageContent = {
  eyebrow: string;
  heading: string;
  summary: string;
  sections: Array<{
    heading: string;
    paragraphs: string[];
  }>;
  relatedLinks: ResourceLink[];
};

function Resource({ link }: { link: ResourceLink }): React.ReactElement {
  if (/^(?:https?:\/\/|mailto:)/i.test(link.url)) {
    return <a href={link.url}>{link.label}</a>;
  }
  return <Link to={link.url}>{link.label}</Link>;
}

export default function TrustPage({ page }: TrustPageProps): React.ReactElement {
  // SAFETY: trustPageContent returns static content matching this local render contract.
  const content = trustPageContent(page) as TrustPageContent;

  return (
    <PageShell className="trust-page" innerClassName="trust-page__inner">
      {/* Static content: settled for the build-time crawler snapshot. */}
      <PrerenderReady ready />
      <header className="trust-page__header">
        <p className="trust-page__eyebrow">{content.eyebrow}</p>
        <h1>{content.heading}</h1>
        <p>{content.summary}</p>
      </header>

      <div className="trust-page__body">
        {content.sections.map((section) => (
          <section key={section.heading}>
            <h2>{section.heading}</h2>
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </section>
        ))}
      </div>

      <nav className="trust-page__resources" aria-label="Related BotSpot resources">
        <h2>Related resources</h2>
        <ul>
          {content.relatedLinks.map((link) => (
            <li key={link.url}>
              <Resource link={link} />
            </li>
          ))}
        </ul>
      </nav>
    </PageShell>
  );
}
